import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";

import { sessionMark } from "stores/sessionGuard";
/**
 * Reports → Developer metrics.
 *
 * The nightly git metrics as live data (API `developer-metrics`, the LATEST
 * run's weekly rows, and `developer-metrics/runs`; plan
 * 2026-10-20_FROM-REPORT-METRICS_developer-metrics-service.md §2.6). Rows live
 * in the two ServiceStores; everything here is a computed view over them —
 * the two tables `summary.md` used to carry: per developer, and "where the
 * effort went" (developer × repo group).
 *
 * Honesty rules this page keeps:
 *  - survival a run did not measure is "not measured", never 0%;
 *  - a developer whose account was deactivated keeps their rows, and is
 *    labelled as having left, in words;
 *  - a developer HQ could not map to a person is shown by the collector's own
 *    name, with the way to fix it (Administration → Git Identities);
 *  - before any run exists the page says so, rather than showing zeros.
 */

/** One weekly fact, as the API serves it. */
export interface DeveloperMetricsRow {
  id: number;
  metricsRunId: number;
  developerKey: string;
  userId: number | null;
  firstName: string | null;
  lastName: string | null;
  userActive: boolean | null;
  repoGroup: string;
  repo: string;
  weekStart: string;
  commits: number;
  aiCommits: number;
  linesAdded: number;
  linesDeleted: number;
  testLinesAdded: number;
  configLinesAdded: number;
  filesTouched: number;
  survivalCohortWeeks: number | null;
  survivalWritten: number | null;
  survivalSurviving: number | null;
}

export interface UnmatchedDeveloper {
  key: string;
  emails: string[];
}

/** One run, as the API serves it. */
export interface DeveloperMetricsRun {
  id: number;
  /** The posting identity (`key:<id>` or `user:<id>`) — an id, not a name. */
  producer: string;
  /** Who posted, for people to read: the key's name (or person's email) at post time. */
  producerName: string | null;
  runId: string;
  asOf: string | null;
  receivedAt: string | null;
  windowWeeks: number;
  repoCount: number;
  commitCount: number;
  rowCount: number;
  developerCount: number;
  unmatchedCount: number;
  unmatched: UnmatchedDeveloper[];
  latest: boolean;
}

/** One developer's totals over the selected weeks and group. */
export interface DeveloperSummary {
  /** `user:<id>` when HQ knows who it is, otherwise `key:<developerKey>`. */
  id: string;
  name: string;
  userId: number | null;
  /** False: their account has been deactivated. Null: HQ could not map them to a person. */
  active: boolean | null;
  commits: number;
  aiCommits: number;
  /** AI-assisted commits / commits; null when there were no commits. */
  aiShare: number | null;
  linesAdded: number;
  linesDeleted: number;
  /** Test lines / lines added; null when no lines were added. */
  testShare: number | null;
  /** Surviving / written over the weeks that measured it; null = not measured. */
  survival: number | null;
  /** The cohort age the survival figure is for (largest seen), when measured. */
  survivalCohortWeeks: number | null;
  /** Commits per repo group. */
  byGroup: Record<string, number>;
}

/** How many of the most recent weeks to show; 0 = every week in the run. */
export const WEEK_RANGES = [4, 8, 12, 0] as const;
export type WeekRange = (typeof WEEK_RANGES)[number];

/** The API's `developer-metrics/runs` page this screen asks for. */
export const RUNS_LIMIT = 20;

const nameOf = (row: DeveloperMetricsRow): string => {
  const full = [row.firstName, row.lastName].filter(Boolean).join(" ").trim();
  return row.userId != null && full ? full : row.developerKey;
};

const ratio = (part: number, whole: number): number | null =>
  whole > 0 ? part / whole : null;

/** "62%" — or "—" for a figure that does not exist. */
export const formatShare = (value: number | null): string =>
  value == null ? "—" : `${Math.round(value * 100)}%`;

/** Summarise weekly rows into one line per developer, most commits first. */
export function summariseDevelopers(
  rows: DeveloperMetricsRow[],
): DeveloperSummary[] {
  const byDev = new Map<
    string,
    DeveloperSummary & {
      testLines: number;
      written: number;
      surviving: number;
      measured: boolean;
    }
  >();
  for (const r of rows) {
    const id = r.userId != null ? `user:${r.userId}` : `key:${r.developerKey}`;
    let d = byDev.get(id);
    if (!d) {
      d = {
        id,
        name: nameOf(r),
        userId: r.userId,
        active: r.userId == null ? null : r.userActive !== false,
        commits: 0,
        aiCommits: 0,
        aiShare: null,
        linesAdded: 0,
        linesDeleted: 0,
        testShare: null,
        survival: null,
        survivalCohortWeeks: null,
        byGroup: {},
        testLines: 0,
        written: 0,
        surviving: 0,
        measured: false,
      };
      byDev.set(id, d);
    }
    d.commits += r.commits;
    d.aiCommits += r.aiCommits;
    d.linesAdded += r.linesAdded;
    d.linesDeleted += r.linesDeleted;
    d.testLines += r.testLinesAdded;
    d.byGroup[r.repoGroup] = (d.byGroup[r.repoGroup] ?? 0) + r.commits;
    if (r.survivalWritten != null && r.survivalSurviving != null) {
      d.measured = true;
      d.written += r.survivalWritten;
      d.surviving += r.survivalSurviving;
      d.survivalCohortWeeks = Math.max(
        d.survivalCohortWeeks ?? 0,
        r.survivalCohortWeeks ?? 0,
      );
    }
  }
  return [...byDev.values()]
    .map(({ testLines, written, surviving, measured, ...d }) => ({
      ...d,
      aiShare: ratio(d.aiCommits, d.commits),
      testShare: ratio(testLines, d.linesAdded),
      survival: measured ? ratio(surviving, written) : null,
      survivalCohortWeeks: measured ? d.survivalCohortWeeks : null,
    }))
    .sort((a, b) => b.commits - a.commits || a.name.localeCompare(b.name));
}

class DeveloperMetricsStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  /** Have we finished a load at least once? Distinguishes "no runs yet" from "not loaded". */
  loaded = false;
  weekRange: WeekRange = 8;
  groupFilter = "all";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private storeFor(path: string, key: string): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore(path) ??
        (this.rootStore as unknown as Record<string, unknown>)[
          key
        ]) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get rowsService(): ServiceStoreType | null {
    return this.storeFor("developer-metrics", "developerMetricsStore");
  }

  private get runsService(): ServiceStoreType | null {
    return this.storeFor("developer-metrics/runs", "developerMetricsRunsStore");
  }

  // ---- Data (entities live in the ServiceStores) ---------------------------

  /**
   * The rows of the run this page is showing — and only those. `load` already
   * asks for that run's rows by id; the filter keeps a cache that still holds
   * another run's rows (a find in flight, a stale page) from being shown under
   * this run's header.
   */
  get rows(): DeveloperMetricsRow[] {
    const run = this.latestRun;
    if (!run) return [];
    return (
      (this.rowsService?.entities ?? []) as unknown as DeveloperMetricsRow[]
    ).filter((r) => r.metricsRunId === run.id);
  }

  get runs(): DeveloperMetricsRun[] {
    return (this.runsService?.entities ?? []) as unknown as DeveloperMetricsRun[];
  }

  /** The run the rows come from. */
  get latestRun(): DeveloperMetricsRun | null {
    return this.runs.find((r) => r.latest) ?? null;
  }

  get hasNoRuns(): boolean {
    return this.loaded && !this.error && this.runs.length === 0;
  }

  /** Every week in the latest run, oldest first. */
  get weeks(): string[] {
    return [...new Set(this.rows.map((r) => r.weekStart))].sort();
  }

  get visibleWeeks(): string[] {
    return this.weekRange === 0 ? this.weeks : this.weeks.slice(-this.weekRange);
  }

  get groups(): string[] {
    return [...new Set(this.rows.map((r) => r.repoGroup))].sort((a, b) =>
      a.localeCompare(b),
    );
  }

  get filteredRows(): DeveloperMetricsRow[] {
    const weeks = new Set(this.visibleWeeks);
    return this.rows.filter(
      (r) =>
        weeks.has(r.weekStart) &&
        (this.groupFilter === "all" || r.repoGroup === this.groupFilter),
    );
  }

  get developers(): DeveloperSummary[] {
    return summariseDevelopers(this.filteredRows);
  }

  /** Columns of the effort grid: the groups present in the selection, busiest first. */
  get effortGroups(): string[] {
    const totals = new Map<string, number>();
    for (const r of this.filteredRows) {
      totals.set(r.repoGroup, (totals.get(r.repoGroup) ?? 0) + r.commits);
    }
    return [...totals.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([g]) => g);
  }

  /** Commits per week in the selection, split AI-assisted / not — the chart. */
  get weeklyTotals(): Array<{ week: string; ai: number; other: number }> {
    return this.visibleWeeks.map((week) => {
      const inWeek = this.filteredRows.filter((r) => r.weekStart === week);
      const commits = inWeek.reduce((n, r) => n + r.commits, 0);
      const ai = inWeek.reduce((n, r) => n + r.aiCommits, 0);
      return { week, ai, other: commits - ai };
    });
  }

  get totals(): { commits: number; aiShare: number | null; developers: number } {
    const commits = this.developers.reduce((n, d) => n + d.commits, 0);
    const ai = this.developers.reduce((n, d) => n + d.aiCommits, 0);
    return {
      commits,
      aiShare: ratio(ai, commits),
      developers: this.developers.length,
    };
  }

  /** Developers in the selection HQ could not map to a person. */
  get unmatchedCount(): number {
    return this.developers.filter((d) => d.userId == null).length;
  }

  /** Did the latest run measure survival at all? */
  get survivalMeasured(): boolean {
    return this.rows.some((r) => r.survivalWritten != null);
  }

  /** Can this person fix an unmatched developer (Git Identities is admin-only)? */
  get canManageIdentities(): boolean {
    return (
      (this.rootStore as unknown as { pageAccessStore?: { isAdminUser?: boolean } })
        .pageAccessStore?.isAdminUser === true
    );
  }

  setWeekRange(value: WeekRange) {
    this.weekRange = value;
  }

  setGroupFilter(value: string) {
    this.groupFilter = value;
  }

  // ---- Loading ----------------------------------------------------------------

  async load() {
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    try {
      // `findOrThrow`: the ServiceStore swallows a failed find (resolves
      // null), which would render "no runs yet" over a failed load.
      //
      // The run list FIRST, then THAT run's rows by id. Two reads that each
      // let the API pick "latest" would, with a run posted between them, put
      // one run's header over another run's rows.
      await findOrThrow(this.runsService, { query: { $limit: RUNS_LIMIT } });
      const run = this.latestRun;
      if (run) {
        await findOrThrow(this.rowsService, {
          query: { metricsRunId: run.id },
        });
      }
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Failed to load developer metrics");
      });
    } finally {
      // A sign-out meanwhile: latching `loaded` over the emptied cache would
      // show the next person a loaded, empty report.
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
          this.loaded = true;
        });
      }
    }
  }

  openGitIdentities() {
    void this.rootStore.routerStore?.goTo("Git Identities");
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.loaded = false;
    this.weekRange = 8;
    this.groupFilter = "all";
  }
}

export default DeveloperMetricsStore;
